import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Field,
  Image as SitecoreImage,
  ImageField,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';

import { ComponentProps, withTheme } from '@gn-audio/jss-components';
import {
  getLocaleWithRegion,
  getPartner,
  getPartners,
  PartnerDetails,
  PartnerOverview,
} from '@gn-audio/jss-core';
import { Option } from '@gn-audio/jss-core/common/models';

import {
  EXPERTISE_MAPPING,
  ExpertiseBadgeColors,
  ExpertiseImageFields,
  ExpertiseOption,
} from 'composables/PartnerLocator/constants';
import { PartnerLocatorDetails } from 'composables/PartnerLocator/PartnerLocatorDetails';
import { PartnerLocatorFilters } from 'composables/PartnerLocator/PartnerLocatorFilters';
import { PartnerLocatorResults } from 'composables/PartnerLocator/PartnerLocatorResults';
import { PartnerLocatorSearchbar } from 'composables/PartnerLocator/PartnerLocatorSearchbar';

export type CountryItem = {
  title: Field<string>;
  value: Field<string>;
  icon: ImageField;
};

type PartnerLocatorFields = {
  // Overview
  availableCountries: CountryItem[];
  dropdownTitle: Field<string>;
  noResultsMessage: Field<string>;
  expertiseFilterTitle: Field<string>;
  nameSearchPlaceholder: Field<string>;
  addressSearchPlaceholder: Field<string>;
  resultsTitle: Field<string>;
  expertiseAllFilterTitle: Field<string>;

  // Details
  authIdTitle: Field<string>;
  addressTitle: Field<string>;
  websiteTitle: Field<string>;
  phoneTitle: Field<string>;

  // Badge Colors
  premiumBadgeColor: Field<string>;
  platinumBadgeColor: Field<string>;
} & ExpertiseImageFields;

export type PartnerLocatorProps = {
  fields: PartnerLocatorFields;
} & ComponentProps;

const PartnerLocator = ({ fields }: PartnerLocatorProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const {
    availableCountries,
    dropdownTitle,
    noResultsMessage,
    expertiseFilterTitle,
    nameSearchPlaceholder,
    addressSearchPlaceholder,
    resultsTitle,
    expertiseAllFilterTitle,

    authIdTitle,
    addressTitle,
    websiteTitle,
    phoneTitle,

    premiumBadgeColor,
    platinumBadgeColor,
  } = fields;

  const badgeColorsMap: ExpertiseBadgeColors = {
    premiumExpert: premiumBadgeColor?.value,
    platinumExpert: platinumBadgeColor.value,
  };

  const countriesOptions: Option[] = useMemo(
    () =>
      availableCountries?.map((country: CountryItem) => ({
        title: country.title?.value,
        value: country.value?.value,
        key: country.value?.value,
        icon: country.icon?.value?.src ? (
          <SitecoreImage
            className="mr-3 inline-block flex-shrink-0"
            field={country.icon.value}
            alt={`${country.title?.value} flag`}
            width={20}
            height={20}
          />
        ) : null,
      })) || [],
    [availableCountries]
  );

  const [selectedCountry, setSelectedCountry] = useState<Option | null>(
    countriesOptions?.[0] || null
  );
  const [partners, setPartners] = useState<PartnerOverview[]>([]);
  const [partnerDetails, setPartnerDetails] = useState<PartnerDetails | null>(null);
  const [searchCompanyValue, setSearchCompanyValue] = useState<string>('');
  const [searchAddressValue, setSearchAddressValue] = useState<string>('');
  const [selectedExpertise, setSelectedExpertise] = useState<ExpertiseOption | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [areDetailsLoading, setAreDetailsLoading] = useState<boolean>(false);
  const [focusedPartnerId, setFocusedPartnerId] = useState<string | null>(null);
  const detailsAbortControllerRef = useRef<AbortController | null>(null);
  const localeWithRegion = getLocaleWithRegion(sitecoreContext.language);
  const region = localeWithRegion.split('-')?.[1]?.toLocaleLowerCase() || '';

  const fetchDetails = async (partnerId: string) => {
    if (detailsAbortControllerRef.current) {
      detailsAbortControllerRef.current.abort();
    }

    const newAbortController = new AbortController();
    detailsAbortControllerRef.current = newAbortController;

    try {
      const details = await getPartner(partnerId, newAbortController);

      if (!newAbortController.signal.aborted) {
        setPartnerDetails(details);
        setAreDetailsLoading(false);
      }
    } catch (error) {
      if (!newAbortController.signal.aborted) {
        console.error('Error fetching partner details:', error);
        setAreDetailsLoading(false);
      }
    }
  };

  const handleShowDetails = (partnerId: string) => {
    setFocusedPartnerId(partnerId);
    setShowDetails(true);
    setAreDetailsLoading(true);
    fetchDetails(partnerId);
  };

  const resetFilters = () => {
    setSearchCompanyValue('');
    setSearchAddressValue('');
    setSelectedExpertise(null);
  };

  useEffect(() => {
    if (region && countriesOptions.length > 0) {
      const countryOption = countriesOptions.find(
        (option) => option.value.toLowerCase() === region
      );

      if (countryOption) {
        setSelectedCountry(countryOption);
      }
    }
  }, [countriesOptions, localeWithRegion, region]);

  useEffect(() => {
    setPartners([]);
    resetFilters();

    const abortController = new AbortController();

    const fetchPartners = async () => {
      setIsLoading(true);

      try {
        const partners = await getPartners(selectedCountry?.value || 'pl', {}, abortController);
        if (!abortController.signal.aborted) {
          setPartners(partners);
          setIsLoading(false);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error fetching partners:', error);
          setIsLoading(false);
        }
      }
    };

    fetchPartners();

    return () => {
      abortController.abort();

      if (detailsAbortControllerRef.current) {
        detailsAbortControllerRef.current.abort();
      }
    };
  }, [selectedCountry]);

  const getPartnerSortPriority = (partner: PartnerOverview): number => {
    const { gnh_audiopartnerprogram, gnh_videopartnerprogram } = partner;

    for (const [code, tier] of Object.entries(EXPERTISE_MAPPING)) {
      if (gnh_audiopartnerprogram?.includes(code) || gnh_videopartnerprogram?.includes(code)) {
        return tier.priority;
      }
    }

    return Object.keys(EXPERTISE_MAPPING).length + 1;
  };

  const filteredPartners = useMemo(() => {
    return partners.filter((partner) => {
      let hasVideoMatch = false;
      let hasAudioMatch = false;

      const companyMatch =
        searchCompanyValue === '' ||
        partner.name?.toLowerCase().includes(searchCompanyValue.toLowerCase());

      const addressMatch =
        searchAddressValue === '' ||
        [partner.address1, partner.address2, partner.city, partner.zip]
          .filter(Boolean)
          .some((field) => field?.toLowerCase().includes(searchAddressValue.toLowerCase()));

      const expertiseMatch = (() => {
        if (!selectedExpertise) return true;

        const selectedExpertiseValues = selectedExpertise?.value
          .split(',')
          .map((val) => val.trim());

        if (selectedExpertise.type === 'video') {
          hasVideoMatch = selectedExpertiseValues.some((expertiseValue) =>
            partner.gnh_videopartnerprogram?.includes(expertiseValue)
          );
        }

        if (selectedExpertise.type === 'audio') {
          hasAudioMatch = selectedExpertiseValues.some((expertiseValue) =>
            partner.gnh_audiopartnerprogram?.includes(expertiseValue)
          );
        }

        return hasVideoMatch || hasAudioMatch;
      })();

      return companyMatch && addressMatch && expertiseMatch;
    });
  }, [partners, searchCompanyValue, searchAddressValue, selectedExpertise]);

  const sortedAndFilteredPartners = useMemo(() => {
    return [...filteredPartners].sort((a, b) => {
      const priorityA = getPartnerSortPriority(a);
      const priorityB = getPartnerSortPriority(b);
      return priorityA - priorityB;
    });
  }, [filteredPartners]);

  return (
    <>
      <div className="container">
        <PartnerLocatorSearchbar
          nameSearchPlaceholder={nameSearchPlaceholder}
          addressSearchPlaceholder={addressSearchPlaceholder}
          searchCompanyValue={searchCompanyValue}
          searchAddressValue={searchAddressValue}
          setSearchCompanyValue={setSearchCompanyValue}
          setSearchAddressValue={setSearchAddressValue}
        />
        <div className="flex flex-col gap-3 text-gray-700 md:flex-row">
          <PartnerLocatorFilters
            partners={partners}
            showFilters={showFilters}
            dropdownTitle={dropdownTitle}
            expertiseFilterTitle={expertiseFilterTitle}
            expertiseAllFilterTitle={expertiseAllFilterTitle}
            countriesOptions={countriesOptions}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            selectedExpertise={selectedExpertise}
            setSelectedExpertise={setSelectedExpertise}
            isLoading={isLoading}
          />
          <PartnerLocatorResults
            filteredPartners={sortedAndFilteredPartners}
            isLoading={isLoading}
            noResultsMessage={noResultsMessage}
            resultsTitle={resultsTitle}
            setShowFilters={setShowFilters}
            showFilters={showFilters}
            handleShowDetails={handleShowDetails}
            showDetails={showDetails}
            focusedPartnerId={focusedPartnerId}
            badgeColorsMap={badgeColorsMap}
            selectedExpertise={selectedExpertise}
          />
        </div>
      </div>
      <PartnerLocatorDetails
        fields={fields}
        partnerDetails={partnerDetails}
        authIdTitle={authIdTitle}
        addressTitle={addressTitle}
        websiteTitle={websiteTitle}
        phoneTitle={phoneTitle}
        showDetails={showDetails}
        setShowDetails={setShowDetails}
        areDetailsLoading={areDetailsLoading}
      />
    </>
  );
};

export default withTheme()(withDatasourceCheck()<PartnerLocatorProps>(PartnerLocator));
